/**
 * Regional Vehicle Cost Calculator Hook
 *
 * Calculates monthly ownership costs with region awareness.
 * All calculations use normalized metric units (km, L/100km).
 * UI layer handles conversion to display units.
 *
 * Related Spec: uk-us-expansion
 * Task: Task 12 - Refactor useVehicleCostCalculator hook for regional formulas
 */

import { useMemo } from 'react';
import {
  CONFIG,
  clampScalingFactor,
  getDistanceMidpoint,
  parseRangeString,
  roundToDecimals,
  validateCostInputs,
} from './calculatorHelpers';

/**
 * Custom hook for calculating vehicle monthly ownership costs
 *
 * @param {Object} props - Calculator input data
 * @param {string} props.estimated_price_range - Price range string (e.g., "€22,000 - €29,500")
 * @param {number} props.estimated_annual_depreciation_percentage - Annual depreciation % (0-100)
 * @param {number} props.fuel_consumption_l_100km_combined - Fuel consumption in L/100km (normalized)
 * @param {string} props.monthly_ownership_cost - Monthly cost range string (e.g., "€100 - €160")
 * @param {string} props.onboarding_annual_distance_value - Distance range ID (e.g., "distance_tier_3")
 * @param {string} props.onboarding_driving_plan - Driving plan from onboarding (e.g., "driving_city_primary")
 * @param {number} props.userInput_monthly_distance_km - User's input monthly distance in km (normalized)
 * @param {number} props.userInput_fuel_price_per_liter - User's input fuel price per liter (or gallon for US)
 * @param {'EU'|'UK'|'US'} props.region - User's region for defaults and validation
 *
 * @returns {Object} Cost breakdown object
 * @returns {number} return.totalCost - Total monthly cost
 * @returns {number} return.depreciationCost - Monthly depreciation cost
 * @returns {number} return.fuelCost - Monthly fuel cost
 * @returns {number} return.maintenanceCost - Monthly maintenance cost
 * @returns {boolean} return.isValid - Whether all inputs were valid
 * @returns {string[]} return.warnings - Array of validation warnings
 * @returns {Object} return.breakdownPercentages - Percentage breakdown of costs
 */
const useVehicleCostCalculator = (props) => {
  const calculatedCosts = useMemo(() => {
    const region = props.region || 'EU';

    // Step 1: Validate and sanitize inputs with region
    const { isValid, sanitized, warnings } = validateCostInputs(props, region);

    // Step 2: Parse price range to get average vehicle price
    const priceRange = parseRangeString(
      sanitized.estimated_price_range,
      25000 // Default average price
    );
    const averageVehiclePrice = priceRange.average;

    // Step 3: Calculate Monthly Depreciation Cost
    // Formula: (Average_Vehicle_Price * (estimated_annual_depreciation_percentage / 100)) / 12
    const depreciationCost = roundToDecimals(
      (averageVehiclePrice * (sanitized.estimated_annual_depreciation_percentage / 100)) / 12
    );

    // Step 4: Calculate Monthly Fuel Cost (Region-Specific Formulas)
    // Apply adjustment based on driving plan from onboarding
    // City driving: +15%, Mixed: +10%, Highway: 0%
    let fuelConsumptionAdjustment = 1.0; // Default: no adjustment

    if (sanitized.onboarding_driving_plan === 'driving_city_primary') {
      fuelConsumptionAdjustment = 1.15; // +15% for city driving
    } else if (sanitized.onboarding_driving_plan === 'driving_balanced_mix') {
      fuelConsumptionAdjustment = 1.1; // +10% for mixed driving
    } else if (sanitized.onboarding_driving_plan === 'driving_highways_long_distance') {
      fuelConsumptionAdjustment = 1.0; // No adjustment for highway
    }
    // Note: driving_offroad_rough_terrain not adjusted (could be handled separately if needed)

    const adjustedFuelConsumption =
      sanitized.fuel_consumption_l_100km_combined * fuelConsumptionAdjustment;

    // Calculate fuel cost using regional formulas
    let fuelCost = 0;

    if (region === 'EU') {
      // EU Formula: (distance_km / 100) * l_100km * price_per_L
      fuelCost = roundToDecimals(
        (sanitized.userInput_monthly_distance_km / 100) *
          adjustedFuelConsumption *
          sanitized.userInput_fuel_price_per_liter
      );
    } else if (region === 'UK') {
      // UK Formula: (distance_miles / mpg_imperial) * 4.546 * price_per_L
      // Convert km to miles
      const distanceMiles = sanitized.userInput_monthly_distance_km * 0.621371;
      // Convert L/100km to Imperial MPG
      const mpgImperial = adjustedFuelConsumption > 0 ? 282.481 / adjustedFuelConsumption : 0;
      // Calculate gallons consumed (UK gallon = 4.546L)
      const gallonsConsumed = mpgImperial > 0 ? distanceMiles / mpgImperial : 0;
      // Convert to liters and multiply by price per liter
      fuelCost = roundToDecimals(gallonsConsumed * 4.546 * sanitized.userInput_fuel_price_per_liter);
    } else if (region === 'US') {
      // US Formula: (distance_miles / mpg_us) * price_per_gal
      // Convert km to miles
      const distanceMiles = sanitized.userInput_monthly_distance_km * 0.621371;
      // Convert L/100km to US MPG
      const mpgUS = adjustedFuelConsumption > 0 ? 235.215 / adjustedFuelConsumption : 0;
      // Calculate cost (price is already per gallon for US)
      fuelCost = roundToDecimals(mpgUS > 0 ? (distanceMiles / mpgUS) * sanitized.userInput_fuel_price_per_liter : 0);
    }

    // Step 5: Calculate Scalable Monthly Maintenance Cost
    // 5a. Get baseline values
    const ownershipCostRange = parseRangeString(
      sanitized.monthly_ownership_cost,
      CONFIG.DEFAULT_VALUES.monthlyOwnershipCost
    );
    const baselineMonthlyMaintenanceCost = ownershipCostRange.average;

    const baselineAnnualDistance = getDistanceMidpoint(
      sanitized.onboarding_annual_distance_value,
      15000
    );
    const baselineMonthlyDistance = baselineAnnualDistance / 12;

    // 5b. Calculate and clamp scaling factor
    // Prevent division by zero
    let scalingFactor = 1.0;
    if (baselineMonthlyDistance > 0) {
      scalingFactor = sanitized.userInput_monthly_distance_km / baselineMonthlyDistance;
      scalingFactor = clampScalingFactor(scalingFactor);
    }

    // 5c. Apply formula: (Baseline * 0.5) + (Baseline * 0.5 * Scaling_Factor)
    const maintenanceCost = roundToDecimals(
      baselineMonthlyMaintenanceCost * 0.3 + baselineMonthlyMaintenanceCost * 0.7 * scalingFactor
    );

    // Step 6: Calculate total cost
    const totalCost = roundToDecimals(depreciationCost + fuelCost + maintenanceCost);

    // Step 7: Calculate percentage breakdown
    const breakdownPercentages = {
      depreciation: totalCost > 0 ? Math.round((depreciationCost / totalCost) * 100) : 0,
      fuel: totalCost > 0 ? Math.round((fuelCost / totalCost) * 100) : 0,
      maintenance: totalCost > 0 ? Math.round((maintenanceCost / totalCost) * 100) : 0,
    };

    // Step 8: Return complete cost object
    return {
      totalCost,
      depreciationCost,
      fuelCost,
      maintenanceCost,
      isValid,
      warnings,
      breakdownPercentages,
    };
  }, [
    props.estimated_price_range,
    props.estimated_annual_depreciation_percentage,
    props.fuel_consumption_l_100km_combined,
    props.monthly_ownership_cost,
    props.onboarding_annual_distance_value,
    props.onboarding_driving_plan,
    props.userInput_monthly_distance_km,
    props.userInput_fuel_price_per_liter,
    props.region,
    props,
  ]);

  return calculatedCosts;
};

export default useVehicleCostCalculator;
